import React, { useState, useEffect } from 'react';

const AdminDashboard = ({ setCurrentView }) => {
  const [stats, setStats] = useState({
    totalCategories: 0,
    totalProducts: 0,
    inStockProducts: 0,
    outOfStockProducts: 0
  });
  const [systemStatus, setSystemStatus] = useState({
    sftpgoStatus: 'unknown',
    sftpgoFiles: 0,
    databaseStatus: 'unknown'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
    fetchSystemStatus();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch categories count
      const categoriesResponse = await fetch('/api/admin/categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const categories = await categoriesResponse.json();
      
      // Fetch products count
      const productsResponse = await fetch('/api/admin/products', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const products = await productsResponse.json();
      
      const inStock = products.filter(p => p.in_stock).length;
      const outOfStock = products.filter(p => !p.in_stock).length;
      
      setStats({
        totalCategories: categories.length,
        totalProducts: products.length,
        inStockProducts: inStock,
        outOfStockProducts: outOfStock
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Check SFTPGo connection
      const sftpgoResponse = await fetch('/api/upload/health', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (sftpgoResponse.ok) {
        const sftpgoData = await sftpgoResponse.json();
        setSystemStatus(prev => ({
          ...prev,
          sftpgoStatus: 'connected',
          sftpgoFiles: sftpgoData.files || 0
        }));
      } else {
        setSystemStatus(prev => ({
          ...prev,
          sftpgoStatus: 'disconnected'
        }));
      }
      
      // Check database connection
      setSystemStatus(prev => ({
        ...prev,
        databaseStatus: 'connected'
      }));
      
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      setSystemStatus(prev => ({
        ...prev,
        sftpgoStatus: 'error',
        databaseStatus: 'error'
      }));
    }
  };

  const handleGoToCategories = () => {
    setCurrentView('categories');
  };

  const handleGoToProducts = () => {
    setCurrentView('products');
  };

  const handleGoToCustomers = () => {
    setCurrentView('customers');
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <h2>Dashboard Overview</h2>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <h3>{stats.totalCategories}</h3>
            <p>Categories</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats.totalProducts}</h3>
            <p>Total Products</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{stats.inStockProducts}</h3>
            <p>In Stock</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{stats.outOfStockProducts}</h3>
            <p>Out of Stock</p>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            <div className="action-card">
              <h4>Add New Category</h4>
              <p>Create a new product category to organize your inventory</p>
              <button className="btn btn-primary" onClick={handleGoToCategories}>
                Go to Categories
              </button>
            </div>
            
            <div className="action-card">
              <h4>Add New Product</h4>
              <p>Add a new product with images and technical details</p>
              <button className="btn btn-primary" onClick={handleGoToProducts}>
                Go to Products
              </button>
            </div>
            
            <div className="action-card">
              <h4>Manage Inventory</h4>
              <p>Update stock levels and product availability</p>
              <button className="btn btn-secondary" onClick={handleGoToProducts}>
                View All Products
              </button>
            </div>
            
            <div className="action-card">
              <h4>Customer Management</h4>
              <p>Review and approve customer access requests</p>
              <button className="btn btn-primary" onClick={handleGoToCustomers}>
                Manage Customers
              </button>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h3>System Status</h3>
          <div className="system-status-grid">
            <div className="status-card">
              <div className="status-icon">
                {systemStatus.sftpgoStatus === 'connected' ? '🟢' : 
                 systemStatus.sftpgoStatus === 'disconnected' ? '🔴' : '🟡'}
              </div>
              <div className="status-content">
                <h4>SFTPGo Storage</h4>
                <p className={`status-text ${systemStatus.sftpgoStatus}`}>
                  {systemStatus.sftpgoStatus === 'connected' ? 'Connected' : 
                   systemStatus.sftpgoStatus === 'disconnected' ? 'Disconnected' : 'Error'}
                </p>
                {systemStatus.sftpgoStatus === 'connected' && (
                  <p className="status-detail">Files: {systemStatus.sftpgoFiles}</p>
                )}
              </div>
            </div>
            
            <div className="status-card">
              <div className="status-icon">
                {systemStatus.databaseStatus === 'connected' ? '🟢' : '🔴'}
              </div>
              <div className="status-content">
                <h4>Database</h4>
                <p className={`status-text ${systemStatus.databaseStatus}`}>
                  {systemStatus.databaseStatus === 'connected' ? 'Connected' : 'Error'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
          <h3>System Information</h3>
          <div className="system-info-list">
            <div className="system-info-item">
              <div className="system-info-label">File Storage:</div>
              <div className="system-info-value">SFTPGo REST API</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">SFTPGo Server:</div>
              <div className="system-info-value">sftpgo.caprover.distracker.com.mx</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Storage User:</div>
              <div className="system-info-value">webstoreuser</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Database:</div>
              <div className="system-info-value">MariaDB 10.x (127.0.0.1:3307)</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Database Name:</div>
              <div className="system-info-value">storedb</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Backend Server:</div>
              <div className="system-info-value">Node.js + Express (Port 5000)</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Frontend:</div>
              <div className="system-info-value">React (Port 3000)</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">API Base URL:</div>
              <div className="system-info-value">http://localhost:5000/api</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">File Upload Limit:</div>
              <div className="system-info-value">Images: 10MB, Videos: 50MB, Docs: 50MB</div>
            </div>
            <div className="system-info-item">
              <div className="system-info-label">Supported Formats:</div>
              <div className="system-info-value">JPG, PNG, GIF, WebP, MP4, PDF, DOC, XLS</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 